<template>
  <AdminLayout>
    <template #content>
      <div class="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h1 class="h4 mb-1">
            Rayon
            <span v-if="rayon" class="text-muted">#{{ rayon.name }}</span>
          </h1>
          <div v-if="rayon" class="small text-muted">
            Créé le {{ $format.datetime(rayon.created_at) }}
          </div>
        </div>

        <div class="d-flex gap-2">
          <button
            type="button"
            class="btn btn-outline-secondary btn-sm btn-icon-label"
            title="Retour à la liste"
            @click="$router.push({ name: 'Rays' })"
          >
            <i class="fa fa-arrow-left"></i>
            <span class="btn-label ms-1">Retour à la liste</span>
          </button>
          <button
            type="button"
            class="btn btn-outline-primary btn-sm btn-icon-label"
            :disabled="!rayon"
            title="Associer des produits"
            @click="$router.push({ name: 'RayProductAssignment', params: { id: rayon.id } })"
          >
            <i class="fa fa-link"></i>
            <span class="btn-label ms-1">Associer des produits</span>
          </button>
          <button
            type="button"
            class="btn btn-outline-primary btn-sm btn-icon-label"
            :disabled="!rayon"
            title="Modifier"
            @click="openEditDialog"
          >
            <i class="fa fa-pen"></i>
            <span class="btn-label ms-1">Modifier</span>
          </button>
        </div>
      </div>

      <div v-if="errorMessage" class="alert alert-danger py-2">
        {{ errorMessage }}
      </div>

      <div v-if="isLoading" class="text-center my-5">
        <i class="fa fa-spinner fa-spin fa-2x mb-2"></i>
        <div class="small text-muted">Chargement du rayon...</div>
      </div>

      <div v-else-if="!rayon && !errorMessage" class="alert alert-warning">
        Rayon introuvable.
      </div>

      <div v-else-if="rayon" class="row g-3 align-items-start">
        <!-- Infos rayon -->
        <div class="col-lg-4">
          <div class="card border-0 shadow-sm rayon-info-card" :style="rayonAccentStyle(rayon.color)">
            <div
              class="card-header bg-white border-0 pb-1 d-flex justify-content-between"
            >
              <h6 class="mb-0">Informations rayon</h6>
              <span
                class="badge"
                :class="rayon.is_active ? 'bg-success' : 'bg-secondary'"
              >
                {{ rayon.is_active ? 'Actif' : 'Inactif' }}
              </span>
            </div>
            <div class="card-body pt-2 pb-3">
              <div class="mb-2">
                <div class="small text-muted">Nom</div>
                <div class="fw-semibold">{{ rayon.name }}</div>
              </div>

              <div v-if="rayon.code" class="mb-2">
                <div class="small text-muted">Code</div>
                <div>{{ rayon.code }}</div>
              </div>

              <div class="mb-2">
                <div class="small text-muted">Couleur</div>
                <div class="rayon-color-display">
                  <span class="rayon-color-dot" :style="{ backgroundColor: rayonColor(rayon.color) }"></span>
                  <span>{{ rayonColor(rayon.color) }}</span>
                </div>
              </div>

              <div class="mb-2">
                <div class="small text-muted">Position</div>
                <div>{{ rayon.position ?? 0 }}</div>
              </div>

              <div v-if="rayon.description" class="mt-2">
                <div class="small text-muted">Description</div>
                <div>{{ rayon.description }}</div>
              </div>

              <hr />

              <div class="mb-1 d-flex justify-content-between">
                <span class="small text-muted">Nombre de produits</span>
                <span class="fw-semibold">{{ products.length }}</span>
              </div>
              <div class="mb-1 d-flex justify-content-between">
                <span class="small text-muted">Valeur de stock (prix de vente)</span>
                <span class="fw-bold">
                  {{ $format.currency(totalSaleValue) }}
                </span>
              </div>
              <div class="mb-1 d-flex justify-content-between">
                <span class="small text-muted">Valeur de stock (prix d’achat)</span>
                <span class="fw-bold">
                  {{ $format.currency(totalPurchaseValue) }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Produits du rayon -->
        <div class="col-lg-8">

            <div
              class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2"
            >
              <h6 class="mb-0">Produits dans ce rayon</h6>
              <small class="text-muted" v-if="products.length">
                {{ products.length }} produit(s)
              </small>
            </div>

              <BootstrapDataTable
                :rows="products"
                :columns="productTableColumns"
                :sort-by="productTable.sortBy"
                :sort-dir="productTable.sortDir"
                detail-route-name="ProductShow"
                empty-text="Aucun produit associé à ce rayon."
                @sort-change="handleProductSortChange"
                @refresh="fetchRay"
              >
                <template #cell-name="{ row }">
                  <div class="fw-semibold">{{ row.name }}</div>
                  <small class="text-muted">{{ row.sku || '-' }}</small>
                </template>

                <template #cell-stock_quantity="{ row }">
                  <span
                    class="badge"
                    :class="row.stock_quantity <= 0 ? 'bg-danger' : 'bg-success'"
                  >
                    {{ row.stock_quantity ?? 0 }}
                  </span>
                </template>

                <template #cell-sale_price="{ row }">
                  {{ $format.currency(row.sale_price || 0) }}
                </template>

                <template #cell-purchase_price="{ row }">
                  {{ $format.currency(row.purchase_price || 0) }}
                </template>

                <template #cell-is_active="{ row }">
                  <span class="badge" :class="row.is_active ? 'bg-success' : 'bg-secondary'">
                    {{ row.is_active ? 'Actif' : 'Inactif' }}
                  </span>
                </template>
              </BootstrapDataTable>


        </div>
      </div>

      <!-- Dialog édition -->
      <PrimevueDialog
        v-model:visible="formDialogVisible"
        :modal="true"
        :style="{ width: '600px' }"
        :closable="false"
      >
        <template #header>
          <h5 class="mb-0">Modifier le rayon</h5>
        </template>

        <RayForm
          v-if="rayon"
          :mode="'edit'"
          :rayon="rayon"
          :submitting="isSavingForm"
          @save="handleSave"
          @cancel="closeFormDialog"
        />
      </PrimevueDialog>
    </template>
  </AdminLayout>
</template>

<script>
import RayForm from './RayForm.vue';
import { normalizeRayonColor, rayonAccentStyle as buildRayonAccentStyle } from '@/utils/rayonColor';

export default {
  name: 'RayShowView',
  components: {
    RayForm,
  },
  data() {
    return {
      rayon: null,
      products: [],
      isLoading: false,
      errorMessage: '',
      successMessage: '',
      formDialogVisible: false,
      isSavingForm: false,
      productTable: {
        sortBy: 'name',
        sortDir: 'asc',
      },
      productTableColumns: [
        { key: 'name', label: 'Produit', sortable: true },
        { key: 'stock_quantity', label: 'Stock', sortable: true, class: 'text-center' },
        { key: 'sale_price', label: 'P. vente', sortable: true, class: 'text-end' },
        { key: 'purchase_price', label: 'P. achat', sortable: true, class: 'text-end' },
        { key: 'is_active', label: 'Statut', sortable: true, class: 'text-center' },
      ],
    };
  },
  computed: {
    totalSaleValue() {
      return this.products.reduce((sum, p) => {
        const qte = p.stock_quantity ?? 0;
        const price = p.sale_price ?? 0;
        return sum + qte * price;
      }, 0);
    },
    totalPurchaseValue() {
      return this.products.reduce((sum, p) => {
        const qte = p.stock_quantity ?? 0;
        const price = p.purchase_price ?? 0;
        return sum + qte * price;
      }, 0);
    },
  },
  mounted() {
    this.fetchRay();
  },
  methods: {
    async fetchRay() {
      const id = this.$route.params.id;

      this.isLoading = true;
      this.errorMessage = '';
      this.successMessage = '';
      this.rayon = null;
      this.products = [];

      try {
        const res = await this.$api.get(`/rayons/${id}`);
        const data = res.data.data;
        this.rayon = {
          id: data.id,
          name: data.name,
          code: data.code,
          color: data.color,
          position: data.position,
          is_active: data.is_active,
          description: data.description,
          created_at: data.created_at,
        };
        this.products = data.products || [];
      } catch (error) {
        console.error('Erreur chargement rayon', error);
        this.errorMessage = 'Impossible de charger ce rayon.';
      } finally {
        this.isLoading = false;
      }
    },

    handleProductSortChange({ sortBy, sortDir }) {
      this.productTable.sortBy = sortBy;
      this.productTable.sortDir = sortDir;
    },

    rayonColor(color) {
      return normalizeRayonColor(color);
    },

    rayonAccentStyle(color) {
      return buildRayonAccentStyle(color);
    },

    openEditDialog() {
      if (!this.rayon) return;
      this.formDialogVisible = true;
    },

    closeFormDialog() {
      this.formDialogVisible = false;
    },

    async handleSave(payload) {
      if (!this.rayon) return;
      if (this.isSavingForm) return;

      this.errorMessage = '';
      this.successMessage = '';
      this.isSavingForm = true;

      try {
        await this.$api.put(`/rayons/${this.rayon.id}`, payload);
        this.successMessage = 'Rayon mis à jour avec succès.';
        this.formDialogVisible = false;
        await this.fetchRay();
      } catch (error) {
        console.error('Erreur mise à jour rayon', error);
        let msg = 'Une erreur est survenue lors de la mise à jour du rayon.';
        if (
          error &&
          error.response &&
          error.response.data &&
          error.response.data.errors
        ) {
          const rawErrors = Object.values(error.response.data.errors);
          const flat = rawErrors.reduce((acc, v) => acc.concat(v), []);
          msg = flat.join(' ');
        }
        this.errorMessage = msg;
      } finally {
        this.isSavingForm = false;
      }
    },
  },
};
</script>

<style scoped>
.card {
  border-radius: 0.75rem;
}

.rayon-info-card {
  border-left: 4px solid var(--rayon-color) !important;
}

.rayon-color-display {
  align-items: center;
  display: inline-flex;
  gap: 0.45rem;
}

.rayon-color-dot {
  border: 1px solid rgba(15, 23, 42, 0.16);
  border-radius: 999px;
  display: inline-block;
  height: 0.8rem;
  width: 0.8rem;
}
</style>
